<template>
  <div ref="evolutionChart" class="evolution-chart"></div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: "ResearchHotspotsEvolution",
  data() {
    return {
      myChart: null,
      option: {},
      researchData: {
        data: [],
        keywords: ['有效成分', '药理作用', '临床应用'],
        colorMap: {
          '有效成分': '#FF6347', // 红色
          '药理作用': '#4682B4', // 蓝色
          '临床应用': '#32CD32'  // 绿色
        }
      }
    };
  },
  methods: {
    // 初始化图表
    initChart() {
      // 首先生成模拟数据
      this.generateResearchData();
      console.log('生成的数据:', this.researchData.data);
      
      // 确保DOM元素已挂载
      if (!this.$refs.evolutionChart) {
        console.error('图表容器未找到');
        return;
      }
      
      console.log('图表容器尺寸:', this.$refs.evolutionChart.clientWidth, this.$refs.evolutionChart.clientHeight);
      this.myChart = echarts.init(this.$refs.evolutionChart);
      
      // 获取近10年的年份数组（用于x轴）
      const years = Array.from({length: 10}, (_, i) => (new Date().getFullYear() - 9 + i).toString());
      
      // 为每个关键词创建单独的数据数组
      const keywordData = {};
      this.researchData.keywords.forEach(keyword => {
        keywordData[keyword] = this.researchData.data
          .filter(item => item.name === keyword)
          .sort((a, b) => a.value[0] - b.value[0]);
      });
      
      this.option = {
        title: {
          text: '近10年中医药研究热点演进',
          left: 'center',
  
          textStyle: {
            color: '#fff',
            fontSize: 16
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            // 找到当前点的所有相关数据
            const year = years[params[0].dataIndex];
            let result = `年份: ${year}<br/>`;
            
            params.forEach(param => {
              const keyword = param.seriesName;
              const dataItem = keywordData[keyword][param.dataIndex];
              if (dataItem) {
                result += `${keyword}: 研究热度 ${dataItem.value[1].toFixed(1)}, 论文数量 ${dataItem.value[2].toFixed(0)} 篇<br/>`;
              }
            });
            
            return result;
          },
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          textStyle: {
            color: '#fff'
          },
          triggerOn: 'mousemove',
          showDelay: 0,
          enterable: true
        },
        legend: {
          data: this.researchData.keywords,
          top: 30,
          textStyle: {
            color: '#fff'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          axisLine: {
            lineStyle: {
              color: '#fff'
            }
          },
          axisLabel: {
            color: '#fff'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '研究热度',
          nameTextStyle: {
            color: '#fff'
          },
          axisLine: {
            lineStyle: {
              color: '#fff'
            }
          },
          axisLabel: {
            color: '#fff'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        series: []
      };
      
      // 为每个关键词添加折线图和散点图
      this.researchData.keywords.forEach(keyword => {
        // 提取该关键词的研究热度数据（用于折线图）
        const lineData = keywordData[keyword].map(item => item.value[1]);
        
        // 提取该关键词的气泡大小数据（论文数量）
        const bubbleSizes = keywordData[keyword].map(item => item.value[2] / 10);
        
        // 添加折线图系列
        this.option.series.push({
          name: keyword,
          type: 'line',
          data: lineData,
          smooth: true,
          symbol: 'emptyCircle',
          symbolSize: 6,
          lineStyle: {
            color: this.researchData.colorMap[keyword],
            width: 3
          },
          itemStyle: {
            color: this.researchData.colorMap[keyword]
          },
          emphasis: {
            focus: 'series',
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        });
        
        // 添加散点图系列（气泡） - 修复数据格式问题
        this.option.series.push({
          name: keyword,
          type: 'scatter',
          data: lineData.map((value, index) => value), // 对于category x轴，scatter只需要y值
          symbolSize: function(data, params) {
            // 使用params.dataIndex获取对应的气泡大小
            return bubbleSizes[params.dataIndex];
          },
          itemStyle: {
            color: this.researchData.colorMap[keyword],
            opacity: 0.8
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 15,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
              opacity: 1
            }
          },
          // 不显示在图例中，避免重复
          showInLegend: false
        });
      });
      
      console.log('ECharts配置:', this.option);
      // 渲染图表
      this.myChart.setOption(this.option);
      console.log('图表渲染完成');
    },
    
    // 生成近10年中医药研究热点数据（模拟）
    generateResearchData() {
      const currentYear = new Date().getFullYear();
      const keywords = this.researchData.keywords;
      const colorMap = this.researchData.colorMap;
      
      const data = [];
      
      // 有效成分数据 - 稳定增长
      data.push(...[
        { name: '有效成分', value: [currentYear - 9, 75, 120], symbolSize: 12, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 8, 82, 140], symbolSize: 14, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 7, 88, 160], symbolSize: 16, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 6, 93, 180], symbolSize: 18, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 5, 98, 200], symbolSize: 20, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 4, 102, 220], symbolSize: 22, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 3, 106, 240], symbolSize: 24, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 2, 110, 260], symbolSize: 26, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear - 1, 113, 280], symbolSize: 28, itemStyle: {color: colorMap['有效成分']} },
        { name: '有效成分', value: [currentYear, 116, 300], symbolSize: 30, itemStyle: {color: colorMap['有效成分']} }
      ]);
      
      // 药理作用数据 - 加速增长
      data.push(...[
        { name: '药理作用', value: [currentYear - 9, 68, 100], symbolSize: 10, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 8, 75, 125], symbolSize: 12.5, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 7, 82, 150], symbolSize: 15, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 6, 89, 180], symbolSize: 18, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 5, 97, 215], symbolSize: 21.5, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 4, 105, 250], symbolSize: 25, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 3, 113, 290], symbolSize: 29, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 2, 121, 330], symbolSize: 33, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear - 1, 128, 370], symbolSize: 37, itemStyle: {color: colorMap['药理作用']} },
        { name: '药理作用', value: [currentYear, 135, 410], symbolSize: 41, itemStyle: {color: colorMap['药理作用']} }
      ]);
      
      // 临床应用数据 - 爆发式增长
      data.push(...[
        { name: '临床应用', value: [currentYear - 9, 62, 80], symbolSize: 8, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 8, 70, 110], symbolSize: 11, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 7, 79, 150], symbolSize: 15, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 6, 89, 200], symbolSize: 20, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 5, 100, 260], symbolSize: 26, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 4, 112, 330], symbolSize: 33, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 3, 125, 410], symbolSize: 41, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 2, 138, 500], symbolSize: 50, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear - 1, 151, 600], symbolSize: 60, itemStyle: {color: colorMap['临床应用']} },
        { name: '临床应用', value: [currentYear, 164, 710], symbolSize: 71, itemStyle: {color: colorMap['临床应用']} }
      ]);
      
      this.researchData.data = data;
    },
    
    // 处理窗口大小变化
    handleResize() {
      if (this.myChart && !this.myChart.isDisposed()) {
        this.myChart.resize();
      }
    }
  },
  mounted() {
    console.log('ResearchHotspotsEvolution组件已挂载');
    // 确保DOM元素已完全渲染后再初始化图表
    this.$nextTick(() => {
      this.initChart();
      // 添加窗口大小变化监听
      window.addEventListener('resize', this.handleResize);
    });
  },
  beforeDestroy() {
    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.handleResize);
    
    // 销毁图表实例
    if (this.myChart && !this.myChart.isDisposed()) {
      this.myChart.dispose();
    }
  }
};
</script>

<style lang="less" scoped>
.evolution-chart {
  width: 100%;
  height: 100%;
  min-height: 400px;
  background-color: rgba(0, 0, 0, 0.1);
}
</style>